import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusTracker, { StatusPill } from "@/components/StatusTracker";
import { BadgeRow } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Star, Send, Clock, DollarSign, CheckCircle2, Paperclip, Sparkles } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [escrow, setEscrow] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState("");
  const [review, setReview] = useState({ rating: 5, text: "" });
  const [payingId, setPayingId] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [attaching, setAttaching] = useState(false);
  const [proposedDate, setProposedDate] = useState("");
  const [rescheduleNote, setRescheduleNote] = useState("");
  const [matches, setMatches] = useState([]);
  const [invited, setInvited] = useState({});
  const [myBizId, setMyBizId] = useState(null);

  useEffect(() => {
    if (user?.role === "business") { api.get("/businesses/me").then((r) => setMyBizId(r.data?.id || null)).catch(() => {}); }
  }, [user]);

  const load = useCallback(async () => {
    const j = await api.get(`/jobs/${id}`).then((r) => r.data).catch(() => null);
    setJob(j);
    if (!j) return;
    setQuotes(await api.get(`/jobs/${id}/quotes`).then((r) => r.data).catch(() => []));
    setEscrow(await api.get(`/jobs/${id}/escrow`).then((r) => r.data).catch(() => null));
    setMessages(await api.get(`/jobs/${id}/messages`).then((r) => r.data).catch(() => []));
    if (["requested", "quoted"].includes(j.status)) {
      setMatches(await api.get(`/jobs/${id}/matches`).then((r) => r.data).catch(() => []));
    } else {
      setMatches([]);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const inviteBusiness = async (bid) => {
    try {
      await api.post(`/jobs/${id}/invite/${bid}`);
      setInvited((m) => ({ ...m, [bid]: true }));
      toast.success("Invitation sent — they'll be notified to quote.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send invite"); }
  };

  const isClient = user && job && user.user_id === job.client_id;
  const isAssignedBiz = !!(myBizId && job && job.accepted_business_id === myBizId);

  const markComplete = async () => {
    try { await api.patch(`/jobs/${id}/status`, { status: "completed" }); toast.success("Updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const payQuote = async (quoteId) => {
    setPayingId(quoteId);
    try {
      const r = await api.post(`/quotes/${quoteId}/checkout`, { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start payment");
      setPayingId(null);
    }
  };

  const release = async () => {
    try { await api.post(`/jobs/${id}/release`); toast.success("Payment released to the business!"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to release"); }
  };

  const submitReview = async () => {
    try { await api.post(`/jobs/${id}/review`, review); toast.success("Thanks for your review!"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to review"); }
  };

  const sendMsg = async () => {
    if (!msg.trim()) return;
    await api.post(`/jobs/${id}/messages`, { text: msg });
    setMsg(""); setMessages(await api.get(`/jobs/${id}/messages`).then((r) => r.data));
  };

  const sendAttachment = async (file) => {
    if (!file) return;
    setAttaching(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/jobs/${id}/messages`, { text: msg || "", attachment_file_id: up.data.file_id });
      setMsg(""); setMessages(await api.get(`/jobs/${id}/messages`).then((r) => r.data));
    } catch { toast.error("Attachment failed"); }
    finally { setAttaching(false); }
  };

  const cancelJob = async () => {
    try {
      const r = await api.post(`/jobs/${id}/cancel`, { reason: cancelReason });
      toast.success(r.data.refunded ? `Cancelled — $${(r.data.refund_amount || 0).toFixed(2)} refunded` : "Job cancelled");
      setCancelOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
  };

  const proposeReschedule = async () => {
    if (!proposedDate) return;
    try {
      await api.post(`/jobs/${id}/reschedule`, { proposed_date: proposedDate, note: rescheduleNote });
      toast.success("New date proposed"); setProposedDate(""); setRescheduleNote(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const respondReschedule = async (accept) => {
    try {
      await api.post(`/jobs/${id}/reschedule/respond`, { accept });
      toast.success(accept ? "New date confirmed" : "Proposal declined"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!job) return <div className="max-w-4xl mx-auto px-4 py-16 text-slate-400">Loading…</div>;

  const paid = escrow && escrow.payment_status === "paid";
  const released = escrow && escrow.released;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-950">{job.title}</h1>
          <p className="mt-1 text-slate-500">{job.trade} · {job.city} · {job.timing}</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusPill status={job.status} />
          {isClient && ["booked", "in_progress"].includes(job.status) && (
            <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
              <DialogTrigger asChild>
                <button data-testid="cancel-job-button" className="text-xs font-semibold text-red-600 hover:underline">Cancel job</button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Cancel this job?</DialogTitle>
                  <DialogDescription>Review the refund policy before confirming.</DialogDescription>
                </DialogHeader>
                <p className="text-sm text-slate-600">
                  {job.status === "booked"
                    ? "Work hasn't started — you'll receive a full refund."
                    : "Work is in progress — our policy refunds 50% of the job amount; the business keeps 50% for work done."}
                </p>
                <Textarea data-testid="cancel-reason-input" className="mt-2" placeholder="Reason (optional)" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
                <DialogFooter>
                  <Button data-testid="cancel-job-confirm" onClick={cancelJob} className="bg-red-600 hover:bg-red-700 text-white rounded-full">Confirm cancellation</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {isAssignedBiz && job.status === "in_progress" && (
        <div data-testid="job-detail-complete-card" className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-slate-700">Finished the work? Mark this job complete so the client can review it and release payment.</p>
          <Button data-testid="job-detail-mark-complete" onClick={markComplete} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap">Mark complete</Button>
        </div>
      )}

      <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm overflow-x-auto">
        <StatusTracker status={job.status} />
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-400">Job details</h3>
        <p className="mt-2 text-slate-700">{job.description}</p>
        {(job.budget_min > 0 || job.budget_max > 0) && (
          <p className="mt-3 text-sm text-slate-500">Budget range: {money(job.budget_min)} – {money(job.budget_max)} CAD</p>
        )}
        {job.photos?.length > 0 && (
          <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="job-photos">
            {job.photos.map((pid) => (
              <a key={pid} href={fileUrl(pid)} target="_blank" rel="noreferrer">
                <img src={fileUrl(pid)} alt="job" className="h-24 w-full object-cover rounded-lg border border-slate-200" />
              </a>
            ))}
          </div>
        )}
      </div>

      {["booked", "in_progress"].includes(job.status) && (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6" data-testid="reschedule-card">
          <h3 className="font-heading font-bold text-slate-900">Scheduling</h3>
          {job.scheduled_date && <p className="mt-1 text-sm text-emerald-700">Confirmed date: {job.scheduled_date}</p>}
          {job.reschedule?.status === "pending" ? (
            job.reschedule.requested_by === user?.user_id ? (
              <p className="mt-3 text-sm text-amber-700">You proposed {job.reschedule.proposed_date}. Waiting for the other party to respond.</p>
            ) : (
              <div className="mt-3">
                <p className="text-sm text-slate-700">New date proposed: <strong>{job.reschedule.proposed_date}</strong>{job.reschedule.note ? ` — ${job.reschedule.note}` : ""}</p>
                <div className="mt-2 flex gap-2">
                  <Button data-testid="reschedule-accept" onClick={() => respondReschedule(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full">Accept</Button>
                  <Button data-testid="reschedule-decline" variant="outline" onClick={() => respondReschedule(false)} className="rounded-full">Decline</Button>
                </div>
              </div>
            )
          ) : (
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <Input type="date" data-testid="reschedule-date-input" value={proposedDate} onChange={(e) => setProposedDate(e.target.value)} className="sm:w-44" />
              <Input data-testid="reschedule-note-input" placeholder="Note (optional)" value={rescheduleNote} onChange={(e) => setRescheduleNote(e.target.value)} />
              <Button data-testid="reschedule-propose" onClick={proposeReschedule} className="bg-slate-950 hover:bg-amber-600 text-white rounded-full whitespace-nowrap">Propose date</Button>
            </div>
          )}
        </div>
      )}

      {/* Escrow panel */}
      {paid && (
        <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-6">
          <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2"><DollarSign size={18} /> Escrow</h3>
          <div className="mt-3 grid sm:grid-cols-3 gap-3 text-sm">
            <div><p className="text-slate-500">Job value</p><p className="font-semibold">{money(escrow.job_amount)}</p></div>
            <div><p className="text-slate-500">Client service fee (3%)</p><p className="font-semibold">{money(escrow.service_fee)}</p></div>
            <div><p className="text-slate-500">Total paid</p><p className="font-semibold">{money(escrow.amount)}</p></div>
          </div>
          <p className="mt-3 text-xs text-slate-500">Business payout on release: {money(escrow.business_payout)} (net of 15% platform commission)</p>
          {released ? (
            <div className="mt-3 text-sm" data-testid="payout-receipt">
              <p className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><CheckCircle2 size={16} /> Funds released</p>
              {escrow.payout_mode === "stripe_transfer" ? (
                <p className="mt-1 text-xs text-emerald-700">Payout sent to the business ✓{escrow.transfer_id ? ` · ${escrow.transfer_id}` : ""}</p>
              ) : (
                <p className="mt-1 text-xs text-amber-700">Payout recorded — it transfers once the business enables Stripe bank payouts.</p>
              )}
            </div>
          ) : isClient && job.status === "completed" ? (
            <Button data-testid="escrow-release-payment-button" onClick={release}
              className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full">Release payment</Button>
          ) : (
            <p className="mt-3 text-sm text-slate-500">Funds held in escrow until the job is marked complete.</p>
          )}
        </div>
      )}

      {/* AI-recommended businesses */}
      {isClient && ["requested", "quoted"].includes(job.status) && matches.length > 0 && (
        <div className="mt-8" data-testid="recommended-businesses">
          <h2 className="font-heading text-2xl font-bold text-slate-950 flex items-center gap-2"><Sparkles size={20} className="text-amber-500" /> Recommended for you</h2>
          <p className="mt-1 text-sm text-slate-500">Best-fit vetted businesses for this job. Invite them to send a quote.</p>
          <div className="mt-4 grid md:grid-cols-2 gap-4">
            {matches.map((b) => (
              <div key={b.id} data-testid={`match-card-${b.id}`} className="rounded-2xl border border-slate-200 bg-white p-6">
                <div className="flex items-center justify-between">
                  <Link to={`/business/${b.id}`} className="font-semibold text-slate-900 hover:text-amber-700">{b.business_name}</Link>
                  <div className="flex items-center gap-2">
                    {b.featured && <span data-testid={`match-featured-${b.id}`} className="inline-flex items-center gap-1 rounded-full bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5"><Sparkles size={10} /> Featured</span>}
                    <div className="flex items-center gap-1 text-amber-600 text-sm font-semibold">
                      <Star size={13} className="fill-amber-500 text-amber-500" />{b.rating > 0 ? b.rating : "New"}
                    </div>
                  </div>
                </div>
                <div className="mt-2"><BadgeRow badges={b.badges} /></div>
                <p className="mt-3 text-sm text-slate-600">{b.reason}</p>
                <div className="mt-4">
                  {b.already_quoted ? (
                    <span className="text-xs font-semibold text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={14} /> Already quoted</span>
                  ) : (b.invited || invited[b.id]) ? (
                    <span className="text-xs font-semibold text-slate-500">Invitation sent ✓</span>
                  ) : (
                    <Button data-testid={`invite-business-${b.id}`} onClick={() => inviteBusiness(b.id)}
                      className="bg-slate-950 hover:bg-amber-600 text-white rounded-full text-sm">Invite to quote</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quotes */}
      <div className="mt-8">
        <h2 className="font-heading text-2xl font-bold text-slate-950">Quotes {quotes.length > 0 && `(${quotes.length})`}</h2>
        {quotes.length === 0 ? (
          <p className="mt-3 text-slate-400">No quotes yet — vetted businesses in {job.city} have been notified.</p>
        ) : (
          <div className="mt-4 grid md:grid-cols-2 gap-4">
            {quotes.map((q) => (
              <div key={q.id} data-testid={`quote-card-${q.id}`}
                className={`rounded-2xl border p-6 bg-white ${q.status === "accepted" ? "border-emerald-400 ring-1 ring-emerald-200" : "border-slate-200"}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-slate-900">{q.business_name}</h3>
                  <div className="flex items-center gap-1 text-amber-600 text-sm font-semibold">
                    <Star size={13} className="fill-amber-500 text-amber-500" />{q.rating > 0 ? q.rating : "New"}
                  </div>
                </div>
                <div className="mt-2"><BadgeRow badges={q.badges} /></div>
                <p className="mt-3 font-heading text-3xl font-extrabold text-slate-950">{money(q.amount)}</p>
                <div className="mt-2 text-xs text-slate-500 space-y-0.5">
                  <p>Labor {money(q.labor)} · Materials {money(q.materials)}</p>
                  <p className="flex items-center gap-1"><Clock size={12} /> Est. {q.timeline_days} days</p>
                </div>
                {q.message && <p className="mt-3 text-sm text-slate-600">{q.message}</p>}
                {q.status === "expired" && <p className="mt-3 text-xs text-red-600 font-semibold">Quote expired</p>}
                {isClient && job.status === "quoted" && q.status === "submitted" && (
                  <Button data-testid={`escrow-pay-button-${q.id}`} disabled={payingId === q.id} onClick={() => payQuote(q.id)}
                    className="mt-4 w-full bg-amber-600 hover:bg-amber-700 text-white rounded-full font-semibold">
                    {payingId === q.id ? "Redirecting…" : `Accept & pay ${money(q.amount * 1.03)}`}
                  </Button>
                )}
                {q.status === "accepted" && <p className="mt-4 text-emerald-700 font-semibold text-sm flex items-center gap-1"><CheckCircle2 size={16}/> Accepted</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Review */}
      {isClient && job.status === "completed" && released && (
        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="font-heading text-xl font-bold text-slate-950">Leave a review</h2>
          <div className="mt-3 flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} data-testid={`review-star-${n}`} onClick={() => setReview((r) => ({ ...r, rating: n }))}>
                <Star size={26} className={n <= review.rating ? "fill-amber-500 text-amber-500" : "text-slate-300"} />
              </button>
            ))}
          </div>
          <Textarea data-testid="review-text-input" className="mt-3" value={review.text} onChange={(e) => setReview((r) => ({ ...r, text: e.target.value }))} placeholder="Share your experience…" />
          <Button data-testid="review-submit-button" onClick={submitReview} className="mt-3 bg-slate-950 hover:bg-amber-600 text-white rounded-full">Submit review</Button>
        </div>
      )}

      {/* Messaging */}
      {(job.status !== "requested") && (
        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="font-heading text-xl font-bold text-slate-950">Messages</h2>
          <div className="mt-4 space-y-3 max-h-72 overflow-y-auto">
            {messages.length === 0 && <p className="text-sm text-slate-400">No messages yet.</p>}
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender_id === user?.user_id ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${m.sender_id === user?.user_id ? "bg-slate-950 text-white" : "bg-slate-100 text-slate-800"}`}>
                  <p className="text-[10px] opacity-70 mb-0.5">{m.sender_name} · {m.sender_role}</p>
                  {m.text}
                  {m.attachment_file_id && (
                    <a href={fileUrl(m.attachment_file_id)} target="_blank" rel="noreferrer" className="block mt-1">
                      <img src={fileUrl(m.attachment_file_id)} alt="attachment" className="max-h-40 rounded-lg border border-white/20" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      <span className="underline text-xs">View attachment</span>
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex gap-2">
            <label className="cursor-pointer flex items-center justify-center h-10 w-10 rounded-full border border-slate-200 hover:border-amber-400 shrink-0">
              <Paperclip size={16} className={attaching ? "text-amber-500 animate-pulse" : "text-slate-500"} />
              <input type="file" className="hidden" data-testid="chat-attach-input" onChange={(e) => sendAttachment(e.target.files[0])} />
            </label>
            <Input data-testid="chat-message-input" value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMsg()} placeholder="Type a message…" />
            <Button data-testid="chat-message-send-button" onClick={sendMsg} className="bg-slate-950 hover:bg-amber-600 text-white rounded-full px-4"><Send size={16} /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
